import { httpClient } from './http-client';
import type { GetPresignedUploadUrlResponse } from '@/api/interfaces/get-presigned-upload-url-response.interface';
import type {
  UploadControllerGetCarDocumentUploadUrlData,
  UploadControllerGetCarImageUploadUrlData,
  UploadControllerGetCleaningFeeUploadUrlData,
  UploadControllerGetContractSignatureUploadUrlData,
  UploadControllerGetDamageMediaUploadUrlData,
  UploadControllerGetInfringementUploadUrlData,
  UploadControllerGetTrainingVideoUploadUrlData,
  UploadControllerGetUserDocumentUploadUrlData,
  UploadControllerGetVehicleTypeImageUploadUrlData,
  UploadControllerGetWorkshopAppointmentDocumentUploadUrlData,
} from '@carvia/ros-client-types';
import axios from 'axios';

export class UploadService {
  endpoint = '/uploads';

  async uploadDamageMedia(
    params: UploadControllerGetDamageMediaUploadUrlData['query'],
    file: string | Blob,
  ) {
    return this.uploadToS3('damage-media', params, file);
  }

  async uploadCarImage(
    params: UploadControllerGetCarImageUploadUrlData['query'],
    file: string | Blob,
  ) {
    return this.uploadToS3('car-image', params, file);
  }

  async uploadVehicleTypeImage(
    params: UploadControllerGetVehicleTypeImageUploadUrlData['query'],
    file: string | Blob,
  ) {
    return this.uploadToS3('vehicle-type-image', params, file);
  }

  async uploadCarDocument(
    params: UploadControllerGetCarDocumentUploadUrlData['query'],
    file: string | Blob,
  ) {
    return this.uploadToS3('car-document', params, file);
  }

  async uploadContractSignature(
    params: UploadControllerGetContractSignatureUploadUrlData['query'],
    file: string | Blob,
  ) {
    return this.uploadToS3('contract-signature', params, file);
  }

  async uploadVideo(
    params: UploadControllerGetTrainingVideoUploadUrlData['query'],
    file: string | Blob,
  ) {
    return this.uploadToS3('video', params, file);
  }

  async uploadCleaningFee(
    params: UploadControllerGetCleaningFeeUploadUrlData['query'],
    file: string | Blob,
  ) {
    return this.uploadToS3('cleaning-fee', params, file);
  }

  async uploadUserDocument(
    params: UploadControllerGetUserDocumentUploadUrlData['query'],
    file: string | Blob,
  ) {
    return this.uploadToS3('user-document', params, file);
  }

  async uploadWorkshopAppointmentDocument(
    params: { workshopAppointmentId: string; fileName: string },
    file: string | Blob,
  ) {
    return this.uploadToS3('workshop-appointment-document', params, file);
  }

  async uploadInfringement(file: string | Blob) {
    const filename = file instanceof File ? file.name : 'upload.jpg';
    return this.uploadToS3('infringement', { filename }, file);
  }

  async uploadToS3(
    endpoint: string,
    params:
      | UploadControllerGetCleaningFeeUploadUrlData['query']
      | UploadControllerGetDamageMediaUploadUrlData['query']
      | UploadControllerGetCarImageUploadUrlData['query']
      | UploadControllerGetVehicleTypeImageUploadUrlData['query']
      | UploadControllerGetCarDocumentUploadUrlData['query']
      | UploadControllerGetContractSignatureUploadUrlData['query']
      | UploadControllerGetTrainingVideoUploadUrlData['query']
      | UploadControllerGetUserDocumentUploadUrlData['query']
      | UploadControllerGetWorkshopAppointmentDocumentUploadUrlData['query']
      | UploadControllerGetInfringementUploadUrlData['query']
      | Record<string, never>,
    file: string | Blob,
  ) {
    const uploadUrl = await httpClient.get<GetPresignedUploadUrlResponse>(
      this.endpoint + '/upload-url/' + endpoint,
      {
        params,
      },
    );
    const { url, fileKey } = uploadUrl.data;

    // Use the actual file MIME type so S3 stores the correct ContentType metadata.
    // Falling back to application/octet-stream for non-Blob inputs (e.g. raw strings).
    const contentType =
      file instanceof Blob ? file.type || 'application/octet-stream' : 'application/octet-stream';
    await axios.put(url, file, {
      headers: { 'Content-Type': contentType },
    });

    return { fileKey };
  }
}

export const uploadService = new UploadService();
